import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../AppIcon';

/**
 * Active-filter chips.
 *
 * Filters currently live inside dropdowns, so once one is set the only way to
 * know it is active is to re-read every control. That is how someone ends up
 * looking at three rows, convinced the rest of their data has vanished.
 *
 * Chips make active state visible, individually removable, and countable — and
 * "Clear all" gives a single escape hatch back to the full set.
 *
 * Renders nothing when no filter is active, so quiet screens stay quiet.
 *
 *   <ActiveFilters
 *     filters={[
 *       { key: 'status', label: 'Status', value: 'Under review', onRemove: () => set('status','all') },
 *       { key: 'q', label: 'Search', value: query, onRemove: () => setQuery('') },
 *     ]}
 *     resultCount={filtered.length}
 *     totalCount={all.length}
 *     onClearAll={reset}
 *   />
 */
export default function ActiveFilters({
  filters = [],
  resultCount,
  totalCount,
  onClearAll,
  className = '',
}) {
  const { t } = useTranslation('common');
  const active = filters.filter((f) => f && f.value !== null && f.value !== undefined && f.value !== '');

  if (active.length === 0) return null;

  const showCount =
    typeof resultCount === 'number' && typeof totalCount === 'number' && totalCount > 0;

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`.trim()}>
      {showCount && (
        <span className="text-xs text-muted-foreground tnum me-1">
          {t('filters.showing', {
            defaultValue: '{{count}} of {{total}}',
            count: resultCount,
            total: totalCount,
          })}
        </span>
      )}

      {active.map((f) => (
        <span
          key={f.key}
          className="inline-flex items-center gap-1.5 h-7 ps-2.5 pe-1 rounded-md border border-border bg-muted/60 text-xs text-foreground"
        >
          {/* The dimension is muted, the value is emphasised — you scan for
              "Under review", not for the word "Status". */}
          <span className="text-muted-foreground">{f.label}</span>
          <bdi className="font-medium">{f.value}</bdi>
          <button
            type="button"
            onClick={f.onRemove}
            aria-label={t('filters.remove', { defaultValue: 'Remove {{name}} filter', name: f.label })}
            className="inline-flex items-center justify-center w-5 h-5 hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={12} />
          </button>
        </span>
      ))}

      {active.length > 1 && onClearAll && (
        <button
          type="button"
          onClick={onClearAll}
          className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
        >
          {t('filters.clearAll', { defaultValue: 'Clear all' })}
        </button>
      )}
    </div>
  );
}
